import { type FC, Fragment, type Ref, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getProjects, type Projects } from '@/features/projects/api/projects';
import {
  getProjectListFilters,
  hasUnsupportedProjectFilters,
} from '@/features/projects/utils/project-filters';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';

import { type Estates, getEstates } from '../api/estates';
import type { EstateCardProps } from './estate-grid-card';
import type { SearchParams } from '@/utils/parseSearchParamsToSearchQuery';
import { Button } from '@/components/ui/button';
import { Button as NewUiButton } from '@/components/new-ui/button';

type EstateAdditionalExtendedInfoProps = {
  creator: string;
  createdAt: string;
};

export type EstateLongCardProps = EstateCardProps &
  EstateAdditionalExtendedInfoProps & { thumbnailId: string };

interface EstatesInfiniteScrollProps {
  filters?: SearchParams;
  filterOwn?: boolean;
  sort?: {
    by?: 'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator';
    order?: 'asc' | 'desc';
  };
  type?: 'estate' | 'project';
  cardRenderer: (estate: Estates[number] | Projects[number]) => JSX.Element;
  /** 'table-row' renders table rows so the component can live inside a tbody. */
  variant?: 'block' | 'table-row';
  colSpan?: number;
}

interface EstatesState {
  [key: string]: Estates[number] | Projects[number];
}

export const EstatesInfiniteScroll: FC<EstatesInfiniteScrollProps> = ({
  filters,
  filterOwn,
  sort,
  type = 'estate',
  cardRenderer,
  variant = 'block',
  colSpan = 1,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [stopLoad, setStopLoad] = useState(false);
  const [estates, setEstates] = useState<EstatesState | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll<HTMLElement>({
    loading,
    stopLoad,
    startPage: 1,
  });

  const loadMoreEstates = useCallback(async () => {
    if (pageNum <= 1) return;

    setLoading(true);
    try {
      setError(null);
      const isNotEmptyFilters = !!(filters && Object.keys(filters).length);
      const estateFilters = filters;
      const projectFilters = getProjectListFilters(filters);
      const isNotEmptyProjectFilters = !!Object.keys(projectFilters).length;

      if (type === 'project' && hasUnsupportedProjectFilters(filters)) {
        setStopLoad(true);
        return;
      }

      const estatesResponse =
        type === 'estate'
          ? await getEstates({
              query: {
                page: pageNum,
                ...(filterOwn && { filterOwn }),
                ...(isNotEmptyFilters && { filters: estateFilters }),
                ...(sort?.by &&
                  sort.order && {
                    sort: {
                      by: sort.by,
                      order: sort.order,
                    },
                  }),
              },
            })
          : await getProjects({
              query: {
                page: pageNum,
                ...(isNotEmptyProjectFilters && { filters: projectFilters }),
                ...(sort?.by &&
                  sort.order && {
                    sort: {
                      by: sort.by,
                      order: sort.order,
                    },
                  }),
              },
            });

      if (!estatesResponse.length) {
        setStopLoad(true);
        return;
      }

      const estatesCollection = estatesResponse.reduce<EstatesState>((acc, v) => {
        acc[v.id] = v;
        return acc;
      }, {});

      setEstates((prev) =>
        prev
          ? {
              ...prev,
              ...estatesCollection,
            }
          : estatesCollection
      );
    } catch (err) {
      setError(err as Error);
      setStopLoad(true);
    } finally {
      setLoading(false);
    }
  }, [filters, filterOwn, pageNum, sort, type]);

  useEffect(() => {
    loadMoreEstates();
  }, [loadMoreEstates]);

  useEffect(() => {
    setEstates(null);
    setError(null);
    resetPageNum();
    setStopLoad(false);
  }, [filters, sort, filterOwn, type, resetPageNum]);

  const isTable = variant === 'table-row';

  const retry = () => {
    setStopLoad(false);
    void loadMoreEstates();
  };

  // the table variant lives inside the new design scope, so it uses the new button
  const retryButton = isTable ? (
    <NewUiButton type="button" aria-label={t('retry')} disabled={loading} onClick={retry}>
      {t('tryAgain')}
    </NewUiButton>
  ) : (
    <Button type="button" aria-label={t('retry')} disabled={loading} onClick={retry}>
      {t('tryAgain')}
    </Button>
  );

  return (
    <Fragment>
      {estates
        ? Object.keys(estates).map((id) => {
            const estate = estates[id];
            const content = estate ? cardRenderer(estate) : null;

            return isTable ? (
              <Fragment key={estate.id}>{content}</Fragment>
            ) : (
              <div key={estate.id}>{content}</div>
            );
          })
        : null}
      {error && stopLoad ? (
        isTable ? (
          <tr>
            <td colSpan={colSpan} className="p-4 text-center">
              {retryButton}
            </td>
          </tr>
        ) : (
          retryButton
        )
      ) : null}
      {!stopLoad ? (
        isTable ? (
          <tr ref={lastElementRef as Ref<HTMLTableRowElement>}>
            <td colSpan={colSpan} className="p-0" />
          </tr>
        ) : (
          <div ref={lastElementRef as Ref<HTMLDivElement>} />
        )
      ) : null}
    </Fragment>
  );
};
